import { useEffect, useRef } from "react";

/* =============================================================================
 * Auto-logout on inactivity (cookie-based, cross-tab)
 *
 * After INACTIVITY_LIMIT_MS of no user activity the app redirects to the
 * WordPress logout URL. The "last activity" timestamp lives in a cookie so it
 * survives tab close/reopen and is shared across tabs (and, in production on a
 * shared parent domain, with WordPress).
 *
 * Nothing sensitive is stored — only a Unix-seconds timestamp.
 * ===========================================================================*/

/* ---- Configuration (change these in one place) --------------------------- */

// How long the user may stay idle before being logged out.
export const INACTIVITY_LIMIT_MS = 60 * 60 * 1000; // 60 minutes

// Cookie that holds the last-activity timestamp (Unix seconds).
const ACTIVITY_COOKIE = "dahrb2b_last_activity";

// How long the cookie itself lives. Must be comfortably LONGER than the idle
// window so that reopening the app after the window (but within a reasonable
// span) still finds the stale timestamp and logs out, instead of the cookie
// having silently expired. Mirrors the 24h auth-token cookie.
const COOKIE_MAX_AGE_SEC = 24 * 60 * 60; // 1 day

// The WordPress logout URL — the single place to change it. Built from the
// project's per-environment `wordpressApiEndPoint`; replace with the real WP
// URL (or a literal like "http://localhost/wordpress/?dahrb2b_logout=1") here.
const WP_BASE = (process.env.wordpressApiEndPoint || "http://localhost/wordpress/").replace(/\/+$/, "/");
export const WP_LOGOUT_URL = `${WP_BASE}?dahrb2b_logout=1`;

// Cookie domain so WordPress + React on sibling subdomains can share one
// cookie in production. Derived from the ACTUAL hostname: null on localhost /
// IP (host-scoped, which is what you want in local dev), or ".dahrb2b.com" on
// a real domain. To force a specific parent domain, set COOKIE_DOMAIN_OVERRIDE.
const COOKIE_DOMAIN_OVERRIDE = null; // e.g. ".dahrb2b.com"
const COOKIE_DOMAIN = (() => {
  if (COOKIE_DOMAIN_OVERRIDE) return COOKIE_DOMAIN_OVERRIDE;
  try {
    const host = (window.location.hostname || "").toLowerCase();
    if (!host || host === "localhost" || host.endsWith(".localhost") ||
        /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null; // host-scoped
    const parts = host.split(".");
    if (parts.length < 2) return null;
    return "." + parts.slice(-2).join("."); // shared parent domain
  } catch (_) {
    return null;
  }
})();

// Throttle cookie writes so frequent events (mousemove/scroll/wheel) don't
// cause overhead — we still reset the countdown, just not on every pixel.
const WRITE_THROTTLE_MS = 10 * 1000;   // at most one cookie write / 10s
const POLL_INTERVAL_MS = 20 * 1000;    // cross-tab expiry check every 20s

/* ---- Safe cookie helpers ------------------------------------------------- */

const nowSec = () => Math.floor(Date.now() / 1000);

const readActivity = () => {
  try {
    const re = new RegExp(`(?:^|;\\s*)${ACTIVITY_COOKIE}=([^;]*)`, "g");
    let best = null;
    let m;
    while ((m = re.exec(document.cookie)) !== null) {
      const n = parseInt(decodeURIComponent(m[1]), 10);
      if (Number.isFinite(n) && n > 0 && (best == null || n > best)) best = n;
    }
    return best;
  } catch (_) {
    return null;
  }
};

// Drop any host-only copy of the cookie so it can't shadow the shared
// parent-domain one (same approach the currency cookie uses).
const eraseHostActivity = () => {
  try {
    document.cookie = `${ACTIVITY_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  } catch (_) {
    /* ignore */
  }
};

const writeActivity = (sec) => {
  try {
    const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
    const domain = COOKIE_DOMAIN ? `; domain=${COOKIE_DOMAIN}` : "";
    // On a shared parent domain, remove the host-only copy first so WordPress and
    // React read the SAME single cookie — exactly like the currency cookie.
    if (COOKIE_DOMAIN) eraseHostActivity();
    document.cookie =
      `${ACTIVITY_COOKIE}=${encodeURIComponent(String(sec))}` +
      `; path=/; max-age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}${domain}`;
  } catch (_) {
    /* cookies blocked — nothing we can do */
  }
};


export const markAutoLogoutActivity = () => {
  writeActivity(nowSec());
};

/**
 * useAutoLogout — attach idle-logout tracking while the user is authenticated.
 * @param {boolean} enabled  Only track while logged in (pass false on public pages).
 */
export const useAutoLogout = (enabled) => {
  // Persist across re-renders AND across React StrictMode's double-invoke.
  const redirectedRef = useRef(false);
  const lastWriteRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    let pollId = null;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const doLogout = () => {
      if (redirectedRef.current) return; // only ever redirect once
      redirectedRef.current = true;
      clearTimer();
      try {
        window.location.replace(WP_LOGOUT_URL);
      } catch (_) {
        window.location.href = WP_LOGOUT_URL;
      }
    };

    // (Re)schedule the logout for the time remaining, based on the cookie.
    const armFromCookie = () => {
      if (redirectedRef.current) return;
      const last = readActivity();
      const lastMs = (last != null ? last : nowSec()) * 1000;
      const remaining = INACTIVITY_LIMIT_MS - (Date.now() - lastMs);
      if (remaining <= 0) {
        doLogout();
        return;
      }
      clearTimer();
      timerRef.current = setTimeout(() => {
        // Re-check at fire time — another tab may have been active meanwhile.
        const again = readActivity();
        const elapsed = Date.now() - (again != null ? again : nowSec()) * 1000;
        if (elapsed >= INACTIVITY_LIMIT_MS) doLogout();
        else armFromCookie();
      }, remaining + 300);
    };

    // Record activity: refresh the cookie (throttled) and re-arm the timer.
    const touch = () => {
      if (redirectedRef.current) return;
      const t = Date.now();
      if (t - lastWriteRef.current < WRITE_THROTTLE_MS) return; // within throttle window
      lastWriteRef.current = t;
      writeActivity(nowSec());
      armFromCookie();
    };

    // On tab focus / return: re-check the cookie BEFORE trusting the old timer.
    const checkOnReturn = () => {
      if (redirectedRef.current) return;
      const last = readActivity();
      if (last == null) {
        // Missing cookie for a logged-in user → initialize safely.
        writeActivity(nowSec());
        lastWriteRef.current = Date.now();
        armFromCookie();
        return;
      }
      if (Date.now() - last * 1000 >= INACTIVITY_LIMIT_MS) doLogout();
      else armFromCookie();
    };

    // --- activity listeners (passive; mousemove/scroll/wheel are throttled) ---
    const onActivity = () => touch();
    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("scroll", onActivity, { passive: true });
    window.addEventListener("wheel", onActivity, { passive: true });
    window.addEventListener("mousedown", onActivity, { passive: true });
    window.addEventListener("click", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity);
    window.addEventListener("touchstart", onActivity, { passive: true });

    // --- tab close/reopen & background → foreground ---
    const onVisible = () => { if (document.visibilityState === "visible") checkOnReturn(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", checkOnReturn);
    window.addEventListener("pageshow", checkOnReturn); // bfcache restore

    // --- cross-tab backup poll: catch expiry even if timers were throttled ---
    pollId = setInterval(() => {
      if (redirectedRef.current) return;
      const last = readActivity();
      if (last == null) return;
      if (Date.now() - last * 1000 >= INACTIVITY_LIMIT_MS) doLogout();
    }, POLL_INTERVAL_MS);

    // --- startup: treat a fresh page-load as activity -----------------------
    // We do NOT log out here on a stale/expired timestamp. A page-load can be a
    // fresh WordPress -> React login whose handoff (token via a shared cookie,
    // no ?token= in the URL) doesn't reset this cookie, and we must never bounce
    // the user straight back out after signing in. So: if the cookie is missing
    // OR already expired, re-stamp "now" and arm a fresh window; otherwise
    // restore the remaining time. Genuine in-app idleness is still enforced by
    // the running timer, the cross-tab poll, and checkOnReturn when the user
    // returns to a backgrounded tab.
    const existing = readActivity();
    if (existing == null || Date.now() - existing * 1000 >= INACTIVITY_LIMIT_MS) {
      writeActivity(nowSec());
      lastWriteRef.current = Date.now();
    }
    armFromCookie();

    return () => {
      clearTimer();
      if (pollId) clearInterval(pollId);
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("scroll", onActivity);
      window.removeEventListener("wheel", onActivity);
      window.removeEventListener("mousedown", onActivity);
      window.removeEventListener("click", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("touchstart", onActivity);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", checkOnReturn);
      window.removeEventListener("pageshow", checkOnReturn);
    };
  }, [enabled]);
};

export default useAutoLogout;
